import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { Button, FieldWithLabel, Input, NumberInput } from '@app/shared/ui/atoms/Common';
import { LoginOptions } from '@app/shared/ui/molecules/Auth';
import { LoginMethod } from '@app/shared/types/common';
import { LoginFormSchema } from '@app/pages/AuthPage';
import { EMAIL_PATTERN } from '@app/shared/constants/common';

interface FormProps {
  form: UseFormReturn<LoginFormSchema>;
  onSubmit: (data: LoginFormSchema) => void;
  isLoading: boolean;
  loginMethod: LoginMethod;
  setLoginMethod: React.Dispatch<React.SetStateAction<LoginMethod>>;
}

export const Form = ({ form, onSubmit, isLoading, loginMethod, setLoginMethod }: FormProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="my-4 flex flex-col gap-4">
      <LoginOptions loginOption={loginMethod} setLoginOption={setLoginMethod} />

      {loginMethod === LoginMethod.TELEGRAM ? (
        <FieldWithLabel label="Telegram ID" error={errors.telegramId?.message} hasErrorField>
          <NumberInput
            {...register('telegramId', {
              required: {
                value: true,
                message: t('auth.errors.telegram_required'),
              },
              minLength: {
                value: 5,
                message: t('auth.errors.telegram_min'),
              },
              maxLength: {
                value: 20,
                message: t('auth.errors.telegram_max'),
              },
            })}
            placeholder={`${t('common.your')} Telegram ID`}
            autoComplete="username"
          />
        </FieldWithLabel>
      ) : (
        <FieldWithLabel label="Email" error={errors.email?.message} hasErrorField>
          <Input
            {...register('email', {
              required: {
                value: true,
                message: t('auth.errors.email_required'),
              },
              pattern: {
                value: EMAIL_PATTERN,
                message: t('auth.errors.email_invalid'),
              },
            })}
            placeholder={`${t('common.your')} Email`}
            autoComplete="email"
          />
        </FieldWithLabel>
      )}

      <FieldWithLabel label={t('auth.password')} error={errors.password?.message} hasErrorField>
        <Input
          type="password"
          {...register('password', {
            required: {
              value: true,
              message: t('auth.errors.password_required'),
            },
            minLength: {
              value: 8,
              message: t('auth.errors.password_value'),
            },
            validate: (value) => value.trim() !== '' || t('auth.errors.password_required'),
          })}
          placeholder={t('auth.your_password')}
          autoComplete="current_password"
        />
      </FieldWithLabel>

      {/*TODO: uncomment when there is a purpose*/}
      {/*<Button variant="text" size="sm" className="text-left" type="button">*/}
      {/*  {t('auth.forgot_password')}*/}
      {/*</Button>*/}

      <Button type="submit" disabled={isLoading}>
        {t('common.enter')}
      </Button>
    </form>
  );
};
